/**
 * CRO UX feature flags — frontend only. Rollback via env without redeploying logic.
 * Set VITE_CRO_*=true in .env.local to enable.
 */

export const CRO_FLAG_ENV_KEYS = {
  CRO_SEARCH_PRIMARY_DETAIL: "VITE_CRO_SEARCH_PRIMARY_DETAIL",
  CRO_CALENDAR_AUTO_OPEN: "VITE_CRO_CALENDAR_AUTO_OPEN",
  CRO_BANNERS_DEFERRED: "VITE_CRO_BANNERS_DEFERRED",
  CRO_DESKTOP_STICKY_BOOKING: "VITE_CRO_DESKTOP_STICKY_BOOKING",
  CRO_MOBILE_SINGLE_CTA: "VITE_CRO_MOBILE_SINGLE_CTA",
  CRO_STICKY_TOTAL_PRICE: "VITE_CRO_STICKY_TOTAL_PRICE",
  /** Calendrier visible sans dates ; résumé + « Modifier » quand plage complète. */
  CRO_CALENDAR_SMART_VISIBILITY: "VITE_CRO_CALENDAR_SMART_VISIBILITY",
  CRO_FUNNEL_QUICK_BOOKING: "VITE_CRO_FUNNEL_QUICK_BOOKING",
  CRO_DEFER_VERIFICATION_BANNER: "VITE_CRO_DEFER_VERIFICATION_BANNER",
  CRO_COMPARE_IN_FILTERS: "VITE_CRO_COMPARE_IN_FILTERS",
  /** Hub tenant progressif : découverte libre, vérification obligatoire avant réservation. */
  CRO_TENANT_VERIFICATION_HUB: "VITE_CRO_TENANT_VERIFICATION_HUB",
  /** Search results: list | map split view (Airbnb-style). */
  CRO_SEARCH_MAP: "VITE_CRO_SEARCH_MAP",
  /** SEO intent pages: show vehicle inventory grid under hero. */
  CRO_SEO_INVENTORY: "VITE_CRO_SEO_INVENTORY",
  /** Guest favorites: localStorage shortlist when logged-out, public /favorites. */
  CRO_GUEST_FAVORITES: "VITE_CRO_GUEST_FAVORITES",
} as const;

export type CroFeatureFlag = keyof typeof CRO_FLAG_ENV_KEYS;

const S1_FLAGS: CroFeatureFlag[] = [
  "CRO_SEARCH_PRIMARY_DETAIL",
  "CRO_CALENDAR_AUTO_OPEN",
  "CRO_BANNERS_DEFERRED",
  "CRO_MOBILE_SINGLE_CTA",
  "CRO_STICKY_TOTAL_PRICE",
  "CRO_CALENDAR_SMART_VISIBILITY",
];

const S3_FLAGS: CroFeatureFlag[] = [
  "CRO_FUNNEL_QUICK_BOOKING",
  "CRO_DEFER_VERIFICATION_BANNER",
  "CRO_COMPARE_IN_FILTERS",
  "CRO_TENANT_VERIFICATION_HUB",
  "CRO_SEARCH_MAP",
];

/**
 * Hub tenant premium : activé par défaut pour livrer le parcours progressif,
 * désactivable via VITE_CRO_TENANT_VERIFICATION_HUB=false (kill-switch).
 */
export const isTenantVerificationHubEnabled = (): boolean => {
  const raw = import.meta.env.VITE_CRO_TENANT_VERIFICATION_HUB as string | undefined;
  if (raw === "false" || raw === "0") {
    return false;
  }
  if (raw === "true" || raw === "1") {
    return true;
  }
  if (isCroS3BundleEnabled()) {
    return true;
  }
  // Opt-out only: progressive flow is the product default.
  return true;
};

const parseEnvBool = (value: string | undefined): boolean =>
  value === "true" || value === "1";

/** Week 1 bundle: enable all S1 quick wins with one env var. */
export const isCroS1BundleEnabled = (): boolean =>
  parseEnvBool(import.meta.env.VITE_CRO_S1_ENABLED);

/** Week 3 bundle: funnel UI coherence (does not include S1/S2). */
export const isCroS3BundleEnabled = (): boolean =>
  parseEnvBool(import.meta.env.VITE_CRO_S3_ENABLED);

export const isCroFlagEnabled = (flag: CroFeatureFlag): boolean => {
  if (isCroS1BundleEnabled() && S1_FLAGS.includes(flag)) {
    return true;
  }
  if (isCroS3BundleEnabled() && S3_FLAGS.includes(flag)) {
    return true;
  }
  const envKey = CRO_FLAG_ENV_KEYS[flag];
  return parseEnvBool(import.meta.env[envKey as keyof ImportMetaEnv]);
};
